import React, {useEffect, useState} from 'react';
import { useVisibilityChange } from '../../Services/helpers';
import Build from '../../Data/Build';
import Alert from '../Alert';
import { useMobile } from '../Mobile';
import { useToasts } from '../Toasts';

const UninstalledUpdatePrompt = ({force = false}) => {
    const [lastCheck, setLastCheck] = useState(null);
    const [outOfDate, setOutOfDate] = useState(false);
    const [latestBuild, setLatestBuild] = useState(null);
    const {isMobile, isVersionAtLeast} = useMobile();

    const isMobileUpdateSupported = isMobile && isVersionAtLeast('2.2.0');

    useVisibilityChange(() => {
        if (updateDue(lastCheck) === false) {
            return;
        }

        checkForUpdates(setOutOfDate, setLastCheck, setLatestBuild);
    }, true);

    useEffect(() => {
        if (updateDue(lastCheck) === false) {
            return;
        }

        checkForUpdates(setOutOfDate, setLastCheck, setLatestBuild);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!outOfDate && !force) {
        return null;
    }

    if (isMobileUpdateSupported) {
        return <ServiceWorkerUpdatePrompt {...{latestBuild}} />;
    }

    return <NotInstalledUpdatePrompt {...{latestBuild}} />;
}

const ServiceWorkerUpdatePrompt = ({latestBuild}) => {
    const {showError} = useToasts();
    const [updateReady, setUpdateReady] = useState(false);
    const {request, updateWorker} = useMobile();

    useEffect(() => {
        const checkForUpdate = async () => {
            try {
                await updateWorker();
                setUpdateReady(true);
            } catch (error) {
                console.error('Failed to check for update', error);
                showError('Update check failed', 'Please try again later.');
            }
        };

        checkForUpdate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMobileUpdate = async () => {
        if (!updateReady) {
            return;
        }

        const rnd = Math.round(Math.random() * 100000).toString(16);
        try {
            request('installUpdate', {
                build: `${latestBuild}-${rnd}`,
                url: `${window.location.protocol}//${window.location.host}/${latestBuild}-${rnd}`
            });
        } catch (error) {
            console.error('Failed to install update:', error);
            showError('Update failed', 'Please try again later.');
        }
    };

    return (
        <Alert
            theme="brand"
            icon="PartyHorn"
            title={updateReady ? 'Update ready' : 'Updating...'}
            message={updateReady ? 'There\'s a new version of getSoapy available. Tap to update.' : 'Downloading the latest version of getSoapy, just a moment...'}
            onClick={handleMobileUpdate}
            loading={!updateReady}
        />
    );
}

const NotInstalledUpdatePrompt = () => {
    return (
        <Alert
            theme="brand"
            icon="PartyHorn"
            title="Update available"
            message="There's a new version of getSoapy available. Tap to update."
            onClick={() => {
                window.location.reload(true);
            }}
        />
    );
}

const timestamp = () => {
    return Math.round(new Date().getTime() / 1000);
}

const updateDue = (lastCheck) => {
    return lastCheck < (timestamp() - (60 * 15));
}

const checkForUpdates = (setOutOfDate, setLastCheck, setLatestBuild) => {
    fetch('/build.txt').then(result => {
        result.text().then(serverBuild => {
            setLatestBuild(serverBuild.trim());
            setOutOfDate(serverBuild.trim() > Build);
            setLastCheck(timestamp());
        })
    })
}

export default UninstalledUpdatePrompt;
